import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type RefObject,
} from 'react'
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor'
import { Editor, Toolbar } from '@wangeditor/editor-for-react'
import '@wangeditor/editor/dist/css/style.css'
import { isDocumentBodyEmpty } from '../lib/documentBodyEmpty'
import { scrollEditorToEnd } from '../lib/scrollEditorToEnd'
import { selectTextInContainer } from '../lib/selectTextInContainer'
import {
  captureEditorCursor,
  restoreEditorCursor,
  getAnchorClientRect,
  getHtmlBeforeAnchor,
  getHtmlAfterAnchor,
  type EditorCursorAnchor,
} from '../lib/editorCursorAnchor'
import './HtmlDocEditor.css'

export type HtmlDocEditorHandle = {
  getSelectionText: () => string
  selectText: (text: string) => boolean
  insertContent: (html: string, mode: 'cursor' | 'append' | 'replace' | 'afterSelection') => void
  setDocumentContent: (html: string) => void
  saveCursorAnchor: () => boolean
  restoreCursorAnchor: () => boolean
  getSavedAnchorRect: () => DOMRect | null
  getContentBeforeCursor: () => string
  getContentAfterCursor: () => string
}

type Props = {
  value: string
  onChange: (html: string) => void
  onBodyEmptyChange?: (empty: boolean) => void
  placeholder?: string
}

export const HtmlDocEditor = forwardRef<HtmlDocEditorHandle, Props>(function HtmlDocEditor(
  { value, onChange, onBodyEmptyChange, placeholder = '输入正文；// 唤起 AI 在光标处写作' },
  ref,
) {
  const [editor, setEditor] = useState<IDomEditor | null>(null)
  const valueRef = useRef(value)
  const onBodyEmptyChangeRef = useRef(onBodyEmptyChange)
  const cursorAnchorRef = useRef<EditorCursorAnchor | null>(null)
  valueRef.current = value
  onBodyEmptyChangeRef.current = onBodyEmptyChange

  const notifyChange = (html: string) => {
    valueRef.current = html
    onChange(html)
    onBodyEmptyChangeRef.current?.(isDocumentBodyEmpty(html, 'html'))
  }

  const syncAfterInsert = (ed: IDomEditor, opts?: { scrollToEnd?: boolean }) => {
    notifyChange(ed.getHtml())
    requestAnimationFrame(() => {
      if (ed.isDestroyed) return
      ed.restoreSelection()
      ed.focus(opts?.scrollToEnd ? true : undefined)
      if (opts?.scrollToEnd) {
        scrollEditorToEnd(ed.getEditableContainer().closest('.html-doc-editor') as HTMLElement | null)
      }
    })
  }

  const toolbarConfig: Partial<IToolbarConfig> = {}
  const editorConfig: Partial<IEditorConfig> = {
    placeholder,
    autoFocus: false,
    // 选区格式条与 AI 浮条重复；顶栏已有格式工具，文本选区不再弹 hoverbar
    hoverbarKeys: {
      text: { menuKeys: [] },
    },
  }

  useImperativeHandle(
    ref,
    () => ({
      getSelectionText: () => editor?.getSelectionText()?.trim() || '',
      selectText: (text: string) => {
        if (!editor || editor.isDestroyed) return false
        const root = editor.getEditableContainer() as HTMLElement | null
        const ok = selectTextInContainer(root, text)
        if (ok) editor.focus()
        return ok
      },
      insertContent: (html: string, mode) => {
        const chunk = html.trim()
        if (!chunk) return
        if (!editor || editor.isDestroyed) {
          notifyChange(mode === 'append' ? (valueRef.current || '') + chunk : chunk)
          return
        }
        editor.focus()
        if (mode === 'append') {
          editor.focus(true)
          editor.dangerouslyInsertHtml(chunk)
          syncAfterInsert(editor, { scrollToEnd: true })
          return
        }
        if (mode === 'afterSelection') {
          const sel = window.getSelection()
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0)
            range.collapse(false)
            sel.removeAllRanges()
            sel.addRange(range)
          }
          editor.dangerouslyInsertHtml(`<p><br></p>${chunk}`)
          syncAfterInsert(editor)
          return
        }
        if (mode === 'replace' && editor.getSelectionText()) {
          editor.dangerouslyInsertHtml(chunk)
        } else {
          editor.dangerouslyInsertHtml(chunk)
        }
        syncAfterInsert(editor)
      },
      setDocumentContent: (html: string) => {
        const next = html.trim()
        if (!next) return
        valueRef.current = next
        if (editor && !editor.isDestroyed) {
          editor.setHtml(next)
        }
        notifyChange(next)
      },
      saveCursorAnchor: () => {
        if (!editor || editor.isDestroyed) return false
        const root = editor.getEditableContainer() as HTMLElement | null
        const anchor = captureEditorCursor(root)
        cursorAnchorRef.current = anchor
        return !!anchor
      },
      restoreCursorAnchor: () => {
        if (!editor || editor.isDestroyed) return false
        const root = editor.getEditableContainer() as HTMLElement | null
        const ok = restoreEditorCursor(cursorAnchorRef.current, root)
        if (ok) editor.focus()
        return ok
      },
      getSavedAnchorRect: () => getAnchorClientRect(cursorAnchorRef.current),
      getContentBeforeCursor: () => {
        if (!editor || editor.isDestroyed) return ''
        const root = editor.getEditableContainer() as HTMLElement | null
        return getHtmlBeforeAnchor(cursorAnchorRef.current, root).trimEnd()
      },
      getContentAfterCursor: () => {
        if (!editor || editor.isDestroyed) return ''
        const root = editor.getEditableContainer() as HTMLElement | null
        return getHtmlAfterAnchor(cursorAnchorRef.current, root).trimStart()
      },
    }),
    [editor, onChange],
  )

  useEffect(() => {
    return () => {
      if (editor == null) return
      editor.destroy()
    }
  }, [editor])

  return (
    <div className="html-doc-editor">
      <Toolbar
        editor={editor}
        defaultConfig={toolbarConfig}
        mode="default"
        className="html-doc-editor-toolbar"
      />
      <Editor
        defaultConfig={editorConfig}
        value={value}
        onCreated={setEditor}
        onChange={(ed) => notifyChange(ed.getHtml())}
        mode="default"
        className="html-doc-editor-body"
      />
    </div>
  )
})

export function useHtmlDocEditorRef(): RefObject<HtmlDocEditorHandle | null> {
  return useRef<HtmlDocEditorHandle | null>(null)
}
